/**
 * Socket.io client context with auto-reconnect and JWT authentication.
 *
 * Wraps the MetricTransport service in a React context so components can:
 * - Subscribe/unsubscribe to server metric rooms
 * - Listen for real-time metric, alert, and status events
 * - Monitor connection state (websocket, polling, disconnected)
 *
 * Automatically connects when the user is authenticated and disconnects on logout.
 * Reconnects with a fresh token if the auth state changes.
 *
 * Validates: Task 21.6 — Setup Socket.io client with auto-reconnect and auth
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  MetricTransport,
  type ConnectionState,
  type MetricsUpdateHandler,
  type DBMetricsUpdateHandler,
  type AlertTriggeredHandler,
  type AlertResolvedHandler,
  type ServerStatusHandler,
} from '../services/metricTransport';
import { useAuth } from './AuthContext';
import { getAccessToken } from '../services/api';

// ─── Context Types ──────────────────────────────────────────────────────────

export interface SocketContextValue {
  /** Current transport connection state */
  connectionState: ConnectionState;
  /** Subscribe to a server's real-time metric updates */
  subscribe: (serverId: string) => void;
  /** Unsubscribe from a server's real-time metric updates */
  unsubscribe: (serverId: string) => void;
  /** Register a handler for system metrics updates */
  onMetricsUpdate: (handler: MetricsUpdateHandler) => () => void;
  /** Register a handler for database metrics updates */
  onDBMetricsUpdate: (handler: DBMetricsUpdateHandler) => () => void;
  /** Register a handler for alert triggered events */
  onAlertTriggered: (handler: AlertTriggeredHandler) => () => void;
  /** Register a handler for alert resolved events */
  onAlertResolved: (handler: AlertResolvedHandler) => () => void;
  /** Register a handler for server status changes */
  onServerStatus: (handler: ServerStatusHandler) => () => void;
}

// ─── Context ────────────────────────────────────────────────────────────────

const SocketContext = createContext<SocketContextValue | null>(null);

// ─── Configuration ──────────────────────────────────────────────────────────

const WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:3001';

// ─── Provider ───────────────────────────────────────────────────────────────

export function SocketProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const transportRef = useRef<MetricTransport | null>(null);

  // Track the company ID for the transport config
  const companyId = user?.companyId ?? '';

  /**
   * Create and connect the transport when authenticated.
   * Disconnect and clean up when auth state changes or on unmount.
   */
  useEffect(() => {
    if (!isAuthenticated || !companyId) {
      // Not authenticated or no company — disconnect if connected
      if (transportRef.current) {
        transportRef.current.disconnect();
        transportRef.current = null;
        setConnectionState('disconnected');
      }
      return;
    }

    const token = getAccessToken();
    if (!token) {
      return;
    }

    // Create a new transport instance
    const transport = new MetricTransport({
      apiBaseUrl: WS_URL,
      token,
      companyId,
    });

    // Listen for connection state changes
    const unsubState = transport.onConnectionStateChange((state) => {
      setConnectionState(state);
    });

    // Connect (will attempt WebSocket first, fall back to polling)
    transport.connect();
    transportRef.current = transport;

    return () => {
      unsubState();
      transport.disconnect();
      transportRef.current = null;
      setConnectionState('disconnected');
    };
  }, [isAuthenticated, companyId]);

  // ─── Stable callbacks ───────────────────────────────────────────────────

  const subscribe = useCallback((serverId: string) => {
    transportRef.current?.subscribe(serverId);
  }, []);

  const unsubscribe = useCallback((serverId: string) => {
    transportRef.current?.unsubscribe(serverId);
  }, []);

  const onMetricsUpdate = useCallback((handler: MetricsUpdateHandler) => {
    if (transportRef.current) {
      return transportRef.current.onMetricsUpdate(handler);
    }
    // Return a no-op unsubscribe if transport isn't ready
    return () => {};
  }, []);

  const onDBMetricsUpdate = useCallback((handler: DBMetricsUpdateHandler) => {
    if (transportRef.current) {
      return transportRef.current.onDBMetricsUpdate(handler);
    }
    return () => {};
  }, []);

  const onAlertTriggered = useCallback((handler: AlertTriggeredHandler) => {
    if (transportRef.current) {
      return transportRef.current.onAlertTriggered(handler);
    }
    return () => {};
  }, []);

  const onAlertResolved = useCallback((handler: AlertResolvedHandler) => {
    if (transportRef.current) {
      return transportRef.current.onAlertResolved(handler);
    }
    return () => {};
  }, []);

  const onServerStatus = useCallback((handler: ServerStatusHandler) => {
    if (transportRef.current) {
      return transportRef.current.onServerStatus(handler);
    }
    return () => {};
  }, []);

  const value: SocketContextValue = {
    connectionState,
    subscribe,
    unsubscribe,
    onMetricsUpdate,
    onDBMetricsUpdate,
    onAlertTriggered,
    onAlertResolved,
    onServerStatus,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

/**
 * Access the socket context for real-time metric subscriptions.
 * Must be used within a SocketProvider (which should be inside AuthProvider).
 */
export function useSocket(): SocketContextValue {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}
